import { EffectCoverflow } from 'swiper/modules';
import { rovingTabs } from '@/utils/roving-tabs';
import { createSlider, syncSlides } from '@/utils/slider';

function revealTab(tab) {
    const scroller = tab.parentElement;
    if (!scroller) {
        return;
    }
    const tabBox = tab.getBoundingClientRect();
    const box = scroller.getBoundingClientRect();
    if (tabBox.left < box.left || tabBox.right > box.right) {
        tab.scrollIntoView({block: 'nearest', inline: 'nearest'});
    }
}

function syncTabs(root, swiper) {
    const currentId = String(swiper.slides[swiper.activeIndex]?.dataset.destinationsSlide ?? '');
    root.querySelectorAll('[data-destinations-tab]').forEach((tab) => {
        const selected = tab.dataset.destinationsTab === currentId;
        if (selected) {
            tab.setAttribute('aria-current', 'true');
            revealTab(tab);
            return;
        }
        tab.removeAttribute('aria-current');
    });
}

document.querySelectorAll('[data-destinations]').forEach((root) => {
    if (root.dataset.destinationsReady === 'true') {
        return;
    }
    const viewport = root.querySelector('[data-destinations-slider]');
    if (!viewport) {
        return;
    }
    root.dataset.destinationsReady = 'true';

    const tabs = [...root.querySelectorAll('[data-destinations-tab]')];
    const wrapper = viewport.querySelector('.swiper-wrapper');
    const originals = [...wrapper.querySelectorAll('[data-destinations-slide]')];

    function viewFor(width) {
        if (!width || window.matchMedia('(max-width: 600px)').matches) {
            return 1;
        }
        const card = window.matchMedia('(max-width: 1000px)').matches ? 395 : 402;
        return Math.round(((width + 30) / (card + 30)) * 100) / 100;
    }

    function setsFor(width) {
        const count = originals.length;
        if (count < 2) {
            return 1;
        }
        const view = viewFor(width);
        if (view <= 1) {
            return 1;
        }
        let perView = Math.ceil(view);
        if (perView % 2 === 0) {
            perView += 1;
        }
        const looped = Math.ceil(perView / 2);
        return Math.max(1, Math.ceil((perView + looped) / count));
    }

    function ensureSets(sets) {
        const want = originals.length * (sets - 1);
        let have = wrapper.querySelectorAll('[data-destinations-clone]').length;
        if (have >= want) {
            return false;
        }
        while (have < want) {
            const source = originals[have % originals.length];
            const clone = source.cloneNode(true);
            clone.setAttribute('data-destinations-clone', '');
            clone.removeAttribute('data-swiper-slide-index');
            clone.removeAttribute('aria-hidden');
            clone.querySelectorAll('[inert]').forEach((node) => node.removeAttribute('inert'));
            wrapper.append(clone);
            have += 1;
        }
        return true;
    }

    const initialWidth = viewport.clientWidth;
    ensureSets(setsFor(initialWidth));
    root.classList.add('is-ready');

    const swiper = createSlider(viewport, {
        modules: [EffectCoverflow],
        prev: root.querySelector('[data-destinations-prev]'),
        next: root.querySelector('[data-destinations-next]'),
        keyboard: { pageUpDown: false },
        effect: 'coverflow',
        centeredSlides: true,
        slidesPerView: viewFor(initialWidth),
        spaceBetween: 30,
        loop: true,
        slideToClickedSlide: true,
        grabCursor: true,
        watchOverflow: true,
        coverflowEffect: {
            rotate: 0,
            stretch: 0,
            depth: 100,
            modifier: 2.5,
            slideShadows: false,
        },
    });

    const applyView = () => {
        const width = swiper.width;
        if (!width) {
            return;
        }
        const view = viewFor(width);
        const sets = setsFor(width);
        const want = originals.length * (sets - 1);
        const have = wrapper.querySelectorAll('[data-destinations-clone]').length;
        if (have >= want && swiper.params.slidesPerView === view) {
            return;
        }
        const realIndex = originals.length ? swiper.realIndex % originals.length : 0;
        swiper.params.slidesPerView = view;
        if (swiper.params.loop) {
            swiper.slideToLoop(realIndex, 0, false);
            swiper.loopDestroy();
        }
        ensureSets(sets);
        if (swiper.params.loop) {
            swiper.loopCreate(realIndex);
        }
        swiper.update();
    };
    applyView();
    swiper.on('resize', applyView);

    const reveal = () => {
        syncSlides(viewport.querySelectorAll('[data-destinations-slide]'), { inertOn: 'controls' });
        syncTabs(root, swiper);
    };
    swiper.on('slideChange', reveal);
    swiper.on('slideChangeTransitionEnd', reveal);
    const paint = () => {
        swiper.update();
        reveal();
    };
    paint();
    requestAnimationFrame(paint);

    rovingTabs(tabs, (index) => swiper.slideToLoop(index));
});
